import { HeroSection } from "./HeroSection";
import { QuoteSection } from "./QuoteSection";
import { CoupleSection } from "./CoupleSection";
import { EventSection } from "./EventSection";
import { GallerySection } from "./GallerySection";
import { ClosingSection } from "./ClosingSection";
import { WelcomeSection } from "./WelcomeSection";
import { LoadingScreen } from "./LoadingScreen";
import { FloatingNav } from "./FloatingNav";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

// Asset imports for preloading
import heroVideo from "@/assets/videos/hero-bg.mp4";
import coverImage from "@/assets/welcome/cover.jpeg";
import groomImage from "@/assets/couple/groom.jpeg";
import brideImage from "@/assets/couple/bride.jpeg";
import photo1 from "@/assets/gallery/photo-1.jpg";
import photo2 from "@/assets/gallery/photo-2.jpg";
import photo3 from "@/assets/gallery/photo-3.jpg";
import photo4 from "@/assets/gallery/photo-4.jpeg";
import photo5 from "@/assets/gallery/photo-5.jpg";
import photo6 from "@/assets/gallery/photo-6.jpeg";
import photo7 from "@/assets/gallery/photo-7.jpeg";
import closingBg from "@/assets/closing/closing-bg.jpeg";
import bgm from "@/assets/audio/bgm.mp3";
import cornerFlower from "@/assets/decoration/corner-flower.png";
import ornamentDivider from "@/assets/decoration/ornament-divider.png";
import floralTexture from "@/assets/decoration/floral-texture.png";

const assetsToPreload = [
  coverImage,
  groomImage,
  brideImage,
  photo1,
  photo2,
  photo3,
  photo4,
  photo5,
  photo6,
  photo7,
  closingBg,
  cornerFlower,
  ornamentDivider,
  floralTexture,
];

export function WeddingApp() {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [heroVideoUrl, setHeroVideoUrl] = useState<string | null>(null);
  const [assetMap, setAssetMap] = useState<Record<string, string>>({});

  // Preloading Logic
  useEffect(() => {
    let isMounted = true;
    const allAssets = [...assetsToPreload, heroVideo, bgm];
    const totalAssets = allAssets.length;
    let settledCount = 0;

    const updateProgress = () => {
      if (!isMounted) return;
      settledCount++;
      const newProgress = Math.min((settledCount / totalAssets) * 100, 100);
      setProgress(newProgress);
    };

    const fetchWithRetry = async (
      url: string,
      retries = 3,
      delay = 1000,
    ): Promise<string> => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch ${url}`);
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } catch (error) {
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          return fetchWithRetry(url, retries - 1, delay * 2);
        }
        throw error;
      }
    };

    const preloadAll = async () => {
      const promises = allAssets.map(async (url) => {
        try {
          // For images/video/audio, we fetch as blob
          const objectUrl = await fetchWithRetry(url);
          if (isMounted) {
            setAssetMap((prev) => ({ ...prev, [url]: objectUrl }));
            if (url === heroVideo) {
              setHeroVideoUrl(objectUrl);
            }
          }
        } catch (error) {
          console.error(`Failed to load asset: ${url}`, error);
          // We intentionally don't throw here inside the map so Promise.allSettled equivalent behavior
          // But we update progress regardless of success/fail
        } finally {
          updateProgress();
        }
      });

      await Promise.all(promises);

      if (isMounted) {
        // Small buffer to show 100%
        setTimeout(() => setIsLoading(false), 800);
      }
    };

    preloadAll();

    // Safety timeout - 60 seconds (increased from 10s for slow connections)
    const timeout = setTimeout(() => {
      if (isMounted && isLoading) {
        console.warn("Loading timed out, forcing app open");
        setIsLoading(false);
      }
    }, 60000);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, []);

  // Lock scroll when welcome screen, or loading is present
  useEffect(() => {
    if (isOpen && !isLoading) {
      document.body.style.overflow = "auto";
    } else {
      document.body.style.overflow = "hidden";
    }
  }, [isOpen, isLoading]);

  // Custom Page Metadata (Title)
  useEffect(() => {
    // Update Title
    const originalTitle = document.title;
    document.title = "Rifa and Nauval Wedding Invitation";

    // Cleanup: Revert to original on unmount
    return () => {
      document.title = originalTitle;
    };
  }, []);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const handleOpenInvitation = () => {
    setIsOpen(true);
    setIsPlaying(true);
  };

  const toggleMusic = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="font-sans antialiased text-stone-900 bg-[#F7F5F2] selection:bg-stone-300">
      <AnimatePresence mode="wait">
        {isLoading && <LoadingScreen key="loading" progress={progress} />}
      </AnimatePresence>

      <AnimatePresence>
        {!isLoading && !isOpen && (
          <WelcomeSection
            onOpen={handleOpenInvitation}
            coverUrl={assetMap[coverImage]}
            ornamentUrl={assetMap[ornamentDivider]}
          />
        )}
      </AnimatePresence>

      {/* Progress Bar */}
      {isOpen && (
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-stone-400 origin-[0%] z-50"
          style={{ scaleX }}
        />
      )}

      {/* Floating Navigation (includes Music Player) */}
      {isOpen && <FloatingNav isPlaying={isPlaying} togglePlay={toggleMusic} />}

      <HeroSection
        videoUrl={heroVideoUrl}
        ornamentUrl={assetMap[ornamentDivider]}
      />
      <QuoteSection cornerFlower={assetMap[cornerFlower]} />
      <CoupleSection
        groomUrl={assetMap[groomImage]}
        brideUrl={assetMap[brideImage]}
        floralTexture={assetMap[floralTexture]}
      />
      <EventSection
        dividerOrnament={assetMap[ornamentDivider]}
        cornerFlower={assetMap[cornerFlower]}
      />
      <GallerySection assetMap={assetMap} />
      <ClosingSection bgUrl={assetMap[closingBg]} />

      <footer className="pt-8 pb-32 text-center text-xs text-stone-400 bg-[#EBE7E0]">
        <p>&copy; 2026 Rifa & Nauval Wedding.</p>
        <p className="mt-1">Built with Love.</p>
      </footer>
    </div>
  );
}
